/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react';
import {Controller} from 'react-hook-form';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {AlertIcon, VisibilityIcon, VisibilityOffIcon} from '@salesforce/retail-react-app/app/components/icons';
import {useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {
  Box, Checkbox, FormControl, FormErrorMessage, FormLabel, IconButton, Input, InputGroup,
  InputRightElement, Select, Text, Textarea,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {AlertErrorIcon, UploadIcon} from '@amblique/king-living/app/components/icons';

const Field = ({
  name,
  label,
  formLabel,
  type = 'text',
  options = [],
  rules = {},
  error,
  placeholder,
  inputProps,
  control,
  autoComplete,
  defaultValue,
  helpText,
  children,
  inputRef,
}) => {
  const intl = useIntl();
  const [hidePassword, setHidePassword] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const PasswordIcon = hidePassword ? VisibilityIcon : VisibilityOffIcon;
  const passwordIconLabel = hidePassword ?
    intl.formatMessage({
      id: 'field.password.assistive_msg.show_password',
      defaultMessage: 'Show password',
    }) :
    intl.formatMessage({
      id: 'field.password.assistive_msg.hide_password',
      defaultMessage: 'Hide password',
    });
  const inputType =
    type === 'password' && hidePassword ? 'password' : type === 'password' ? 'text' : type;

  const styles = useMultiStyleConfig('Field', {});

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      rules={rules}
      render={({field: {onChange, value, ref}}) => {
        const _inputProps =
          typeof inputProps === 'function' ? inputProps({value, onChange}) : inputProps;

        const handleFocus = (e) => {
          setIsFocused(true);
          _inputProps?.onFocus?.(e);
        };

        const handleBlur = (e) => {
          setIsFocused(false);
          _inputProps?.onBlur?.(e);
        };

        return (
          <FormControl id={name} isInvalid={error}>
            <InputGroup {...styles.inputGroup}>
              {['text', 'password', 'email', 'phone', 'tel', 'number'].includes(
                type
              ) && (
                <Input
                  ref={(node) => {
                    ref(node);
                    if (inputRef) inputRef.current = node;
                  }}
                  autoComplete={autoComplete}
                  placeholder={placeholder || label}
                  type={inputType}
                  value={value}
                  {..._inputProps}
                  {...styles.input}
                  onBlur={handleBlur}
                  onChange={onChange}
                  onFocus={handleFocus}
                />
              )}

              {type === 'hidden' && (
                <input
                  ref={ref}
                  type="hidden"
                  value={value}
                  onChange={onChange}
                  {..._inputProps}
                />
              )}

              {type === 'password' && (
                <InputRightElement {...styles.passwordIconButtonContainer}>
                  <IconButton
                    aria-label={passwordIconLabel}
                    icon={<PasswordIcon {...styles.passwordIcon} />}
                    variant="ghosted"
                    onClick={() => setHidePassword(!hidePassword)}
                    {...styles.passwordIconButton}
                  />
                </InputRightElement>
              )}

              {type === 'select' && (
                <Select
                  ref={ref}
                  placeholder={placeholder || label}
                  value={value}
                  onChange={onChange}
                  {..._inputProps}
                  {...styles.select}
                >
                  {options.map((opt) => (
                    <option key={`${opt.label}-${opt.value}`} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              )}

              {type === 'textarea' && (
                <Textarea
                  ref={(node) => {
                    ref(node);
                    if (inputRef) inputRef.current = node;
                  }}
                  placeholder={placeholder || label}
                  value={value}
                  onChange={onChange}
                  {..._inputProps}
                  {...styles.textarea}
                />
              )}

              {type === 'checkbox' && (
                <Checkbox
                  ref={ref}
                  isChecked={value}
                  onChange={(e) => onChange(e.target.checked)}
                  {..._inputProps}
                  {...styles.checkbox}
                >
                  {formLabel || label}
                </Checkbox>
              )}

              {type === 'file' && (
                <Box display="flex" flexDirection="column" position="relative" width="100%">
                  <Box
                    as="label"
                    htmlFor={name}
                    {...styles.fileInputContainer}
                    sx={error ? {
                      borderWidth: '2px', // Keep 2px to maintain consistent size
                      borderColor: 'brandColors.greyscale10',
                      backgroundColor: 'white',
                      px: 0,
                    } : {}}
                  >
                    <input
                      ref={ref}
                      id={name}
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];

                        onChange(file);
                      }}
                      {..._inputProps}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer',
                        zIndex: 1,
                      }}
                    />
                    <UploadIcon {...styles.fileInputIcon} />
                    <Text {...styles.fileInputPrimaryText}>
                      Upload a file or drag and drop
                    </Text>
                    <Text {...styles.fileInputSecondaryText}>
                      Formats accepted: JPG, PNG or PDF
                      <br />
                      File size must not exceed 20MB
                    </Text>
                  </Box>
                  {error && (
                    <Box {...styles.fileInputErrorAlert}>
                      <AlertErrorIcon aria-hidden="true" {...styles.fileInputErrorAlertIcon} />
                      <Text>{error.message}</Text>
                    </Box>
                  )}
                </Box>
              )}

              {!['checkbox', 'radio', 'hidden', 'file', 'textarea', 'select'].includes(type) &&
                (formLabel || (
                  <FormLabel
                    {...styles.label}
                    sx={{
                      ...styles.label.sx,
                      opacity: isFocused || (value && value.toString().trim() !== '') ? 1 : 0,
                      top: isFocused || (value && value.toString().trim() !== '') ? 2 : 4.5,
                      color: error ? 'brandColors.error' : isFocused || (value && value.toString().trim() !== '') ? 'brandColors.primaryDarkLinen' : 'brandColors.greyscale80',
                    }}
                  >
                    {label}
                  </FormLabel>
                ))}

              {children}
            </InputGroup>

            {error && type !== 'hidden' && type !== 'file' && (
              <FormErrorMessage color="red.600">
                <AlertIcon aria-hidden="true" mr={2} />
                {error.message}
              </FormErrorMessage>
            )}

            {helpText}
          </FormControl>
        );
      }}
    />
  );
};

Field.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  autoComplete: PropTypes.string,
  formLabel: PropTypes.any,
  type: PropTypes.oneOf([
    'text',
    'number',
    'password',
    'email',
    'phone',
    'tel',
    'select',
    'checkbox',
    'hidden',
    'file',
    'textarea'
  ]),
  options: PropTypes.arrayOf(PropTypes.shape({label: PropTypes.string, value: PropTypes.any})),
  rules: PropTypes.object,
  error: PropTypes.shape({message: PropTypes.string}),
  placeholder: PropTypes.string,
  inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  control: PropTypes.object,
  defaultValue: PropTypes.any,
  helpText: PropTypes.any,
  children: PropTypes.any,
  inputRef: PropTypes.object,
};

export default Field;
